// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import api from '../api/axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

const ExpenseTrendChart = ({ period = 'monthly' }) => {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');

    api
      .get(`/api/budget/reports/expense/trend/?period=${period}`)
      .then((response) => {
        const data = response.data;

        // Format labels based on period
        const labels = (data ?? []).map((item) => {
          if (period === 'monthly') {
            // item.period is in format "2026-01-01T00:00:00Z" or "2026-01"
            return new Date(item.period).toLocaleString('en-IN', {
              month: 'short',
              year: 'numeric',
            });
          }
          return new Date(item.period).getFullYear().toString();
        });

        const expenses = (data ?? []).map((item) => item.total || 0);

        setChartData({
          labels,
          datasets: [
            {
              label: `Total Expenses (${period.charAt(0).toUpperCase() + period.slice(1)})`,
              data: expenses,
              borderColor:
                period === 'monthly'
                  ? 'rgba(99, 102, 241, 1)'
                  : 'rgba(236, 72, 153, 1)',
              backgroundColor:
                period === 'monthly'
                  ? 'rgba(99, 102, 241, 0.2)'
                  : 'rgba(236, 72, 153, 0.2)',
              borderWidth: 3,
              tension: 0.4,
              fill: true,
              pointBackgroundColor:
                period === 'monthly'
                  ? 'rgba(99, 102, 241, 1)'
                  : 'rgba(236, 72, 153, 1)',
              pointBorderColor: '#fff',
              pointBorderWidth: 2,
              pointRadius: 6,
              pointHoverRadius: 8,
            },
          ],
        });
      })
      .catch((err) => {
        setChartData(null);
        setError(
          err?.response?.data?.detail ||
            'Failed to load the expense trend. Please try again.',
        );
      })
      .finally(() => setLoading(false));
  }, [period]);

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: `Expense Trend (${period.charAt(0).toUpperCase() + period.slice(1)})`,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 11,
            family: 'Inter, sans-serif',
          },
          color: '#6B7280',
          callback: function (value) {
            return '₹' + value.toLocaleString('en-IN');
          },
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11,
            family: 'Inter, sans-serif',
          },
          color: '#6B7280',
        },
      },
    },
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-500">Loading chart...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3 bg-red-50 border border-red-200 rounded-md">
        <p className="text-red-700 text-sm">{error}</p>
      </div>
    );
  }

  if (!chartData || chartData.labels.length === 0) {
    return (
      <div className="flex items-center justify-center py-8 text-sm text-gray-400">
        No expense data for this period yet.
      </div>
    );
  }

  return <Line data={chartData} options={options} />;
};

export default ExpenseTrendChart;
